import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property, query } from 'lit/decorators.js';
import componentStyles from './se-capabilities-block.css.lit';
import './se-capabilities-list-item';

import {
  SeCapabilitiesBlockItemType,
  Capability,
} from './se-capabilities-block';
/**
 * The SeCapabilitiesList component
 * @element se-capabilities-list
 */
@customElement('se-capabilities-list')
export class SeCapabilitiesList extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, reflect: true })
  type: SeCapabilitiesBlockItemType;

  /**
   * the data array passed in from the parent element.
   */
  @property({ type: Array })
  capability: Capability;

  /**
   * value of --open-height css var
   * initially 0
   */
  @property({ type: Number })
  openHeight = 0;

  /**
   * value of --closed-height css var
   * initially 0
   */
  @property({ type: Number })
  closedHeight = 0;

  /**
   * Open status of the menu if there are more than 4 list items.
   */
  @property({ type: Boolean })
  isOpen = false;

  /**
   * ref to the ul element.
   */
  @query('.capabilities-block--list')
  listElement: HTMLElement;

  /**
   * Reference to height of each list item referenced by id.
   */
  @property({ type: Object })
  heights: Record<string, number> = {};

  /**
   * Value of --transition css var, and .
   */
  @property({ type: Number })
  transition = 300;

  /**
   * Array of ids for items that need to hide/show.
   */
  hiddenElementIDs: string[] = [];

  /**
   * used to provide a unique id.
   */
  seed: string = Math.floor(Math.random() * 10000).toString();

  render(): TemplateResult {
    const name = this.type.charAt(0).toUpperCase() + this.type.slice(1);

    return html`
      <div class="capabilities-block--main-item">
        <span class="capabilities-block--main-item-label">${name}</span>
        <ul class="capabilities-block--list">
          ${this.capability.map((item, i) => {
            if (i >= 4) {
              this.hiddenElementIDs.push(`${this.type}-${this.seed}-${i}`);
              return html`
                <li class="capabilities-block--item">
                  <se-capabilities-list-item
                    toHide
                    text=${item.text}
                    url=${item.url}
                    type=${this.type}
                    .parent=${this}
                    id="${this.type}-${this.seed}-${i}"
                  ></se-capabilities-list-item>
                </li>
              `;
            } else {
              return html` <li class="capabilities-block--item">
                <se-capabilities-list-item
                  text=${item.text}
                  url=${item.url}
                  type=${this.type}
                  .parent=${this}
                  id="${this.type}-${this.seed}-${i}"
                ></se-capabilities-list-item>
              </li>`;
            }
          })}
        </ul>
        ${this.capability.length > 4 ? this.seeMoreButton() : null}
      </div>
    `;
  }

  firstUpdated() {
    this.setInitialHeight();
  }

  updated() {
    this.setOpenHeight();
    this.setClosedHeight();
    this.setCSSVars();
  }

  /**
   * method to be called by the child element if it updates its height after initial render.
   * Updates the css vars, and then the component.
   */
  childInitiatedUpdate() {
    this.setCSSVars();
    this.requestUpdate();
  }

  /**
   * Adds the initial height of the ul element. Basically padding/margins.
   */
  setInitialHeight() {
    this.heights.init = this.listElement.getBoundingClientRect().height;
  }

  /**
   * filters out the elements to hide/show from the heights object and
   * sets the closedHeight attribute to the accumulated value of the remaining elements.
   */
  setClosedHeight() {
    const notHidden = Object.keys(this.heights).filter(
      key => !this.hiddenElementIDs.includes(key)
    );
    this.closedHeight = notHidden
      .map(id => this.heights[id])
      .reduce((a, b) => a + b);
  }

  /**
   * sets the openHeight attribute to the cumulative value of the recorded elements.
   */
  setOpenHeight() {
    this.openHeight = Object.values(this.heights).reduce((a, b) => a + b);
  }

  /**
   * generates the button if required
   * @returns TemplateResult
   */
  seeMoreButton() {
    return html`
      <button
        class="capabilities-block--button"
        @click=${this.toggleOpen}
        aria-haspopup="true"
        aria-expanded=${this.isOpen}
        aria-label="see ${this.isOpen ? 'less' : 'all'} ${this.type}"
      >
        ${this.setButtonText()} ${this.setButtonIcon()}
      </button>
    `;
  }

  /**
   * returns the button text
   * @returns TemplateResult
   */
  setButtonText() {
    if (this.isOpen) {
      return html`<span>See less</span>`;
    } else {
      return html`<span>See all</span>`;
    }
  }

  /**
   * return the correct button icon.
   * @returns TemplateResult
   */
  setButtonIcon() {
    if (this.isOpen) {
      return html`
        <outline-icon
          slot="right"
          size=".75em"
          name="dash"
          library="system"
          color="brand-primary-2"
        ></outline-icon>
      `;
    } else {
      return html` <outline-icon
        slot="right"
        size=".75em"
        name="plus"
        library="system"
        color="brand-primary-2"
      ></outline-icon>`;
    }
  }

  /**
   * Toggles the open class on the ul element, and either removes
   * the hidden class or  adds it to the hide show elements
   * after a delay matching the transition time set in the css.
   * Finally toggles the isOpen attribute.
   * If opening, passes focus to the first hidden element.
   */
  toggleOpen() {
    if (this.isOpen) {
      this.hiddenElementIDs.forEach(id => {
        const subLink = this.shadowRoot
          ?.querySelector(`#${id}`)
          ?.shadowRoot?.querySelector(`#${id}-link`);
        setTimeout(() => subLink?.classList.add('hidden'), this.transition);
      });
      this.listElement.classList.toggle('open');
    }

    if (!this.isOpen) {
      // get first hidden element to apply focus on open
      const firstHidden = this.shadowRoot
        ?.querySelector(`#${this.hiddenElementIDs[0]}`)
        ?.shadowRoot?.querySelector(
          `#${this.hiddenElementIDs[0]}-link`
        ) as HTMLElement;

      this.hiddenElementIDs.forEach(id => {
        const subLink = this.shadowRoot
          ?.querySelector(`#${id}`)
          ?.shadowRoot?.querySelector(`#${id}-link`);
        subLink?.classList.remove('hidden');
      });

      this.listElement.classList.toggle('open');

      setTimeout(() => firstHidden.focus(), this.transition);
    }

    this.isOpen = !this.isOpen;
  }

  /**
   * Once the open/closedHeight attributes are set, creates a local
   * css var for each height.
   */
  setCSSVars() {
    if (this.closedHeight > 0) {
      this.style.setProperty('--open-height', `${this.openHeight}px`);
      this.style.setProperty('--closed-height', `${this.closedHeight}px`);
      this.style.setProperty('--transition', `${this.transition}ms`);
    }
  }
}
